import { Component, inject, OnInit, signal } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { EMPTY } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { MessagingService } from '../components/messaging/messaging.service';
import { RedirectHelper } from '../helpers/redirect.helper';
import { CurrentUserService } from '../services/currentUser.service';
import { NoticeService } from '../services/notice.service';
import { SysAdminService } from '../services/sys-admin.service';
import { ToolbarService } from '../services/toolbar.service';
import { UserService } from '../services/user.service';
import { InfoMessageComponent } from '../components/info-message/info-message.component';
import { MatIcon } from '@angular/material/icon';
import { MatError, MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatToolbar, MatToolbarRow } from '@angular/material/toolbar';
import { MatInput } from '@angular/material/input';
import { BrowserAutoFillDisabledDirective } from '../directives/browser-auto-fill-disabled.directive';
import { MatButton, MatIconButton } from '@angular/material/button';
import { StaticFooterComponent } from '../static-footer/static-footer.component';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  imports: [
    InfoMessageComponent,
    MatIcon,
    MatToolbar,
    MatToolbarRow,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    BrowserAutoFillDisabledDirective,
    MatError,
    MatIconButton,
    MatSuffix,
    MatButton,
    StaticFooterComponent
  ]
})
export class LoginComponent implements OnInit {
  which: 'LOGIN' | 'RESET' = 'LOGIN';
  loginForm: UntypedFormGroup;
  unauthorized = signal<string>('');
  hidePassword = true;

  #auth = inject(AuthService);
  #router = inject(Router);
  #route = inject(ActivatedRoute);
  #message = inject(MessagingService);
  #userService = inject(UserService);
  #redirectHelper = inject(RedirectHelper);
  #currentUserService = inject(CurrentUserService);
  #toolbarService = inject(ToolbarService);
  #sysAdminService = inject(SysAdminService);
  #noticeService = inject(NoticeService);

  ngOnInit() {
    this.#toolbarService.pausePingInterval.next(true);
    this.loginForm = new UntypedFormGroup({
      username: new UntypedFormControl('', [Validators.required]),
      password: new UntypedFormControl('', [Validators.required])
    });
  }

  login() {
    const username: string = this.loginForm.get('username').value;
    const password: string = this.loginForm.get('password').value;
    this.#auth.login({ username, password }).pipe(
      catchError((error) => {
        if (error.status === 403) {
          if (error.error.errorCode === 'FAILED_ATTEMPT') {
            this.unauthorized.set('Your username or password could not be validated. Please review your information.');
          } else if (error.error.errorCode === 'LOCKED') {
            this.unauthorized.set('Too many attempts, your account has been locked. Please try again in 2 minutes.');
          } else if (error.error.errorCode === 'USER_NOT_ENABLED') {
            this.unauthorized.set(
              'Your account information could not be verified. Please contact your System Administrator.'
            );
          }
        } else {
          this.#message.error('There was a problem logging you in. Please try again.');
        }
        return EMPTY;
      }),
      tap(() => this.#toolbarService.pausePingInterval.next(false)),
      switchMap(() => this.#userService.getCurrentUser().pipe(
        catchError((err) => {
          console.error('Failed to fetch current user:', err);
          this.#message.error('There was a problem logging you in. Please try again.');
          return EMPTY;
        })
      )),
      tap((user) => {
        this.#currentUserService.userLogin(user);
        if (this.#currentUserService.currentUserValue?.eulaApproved !== false) {
          this.#noticeService.fetchUnseenNoticesIfPermissible();
        }
        this.revokeAccessRepairerSite();
      })
    ).subscribe();
  }

  reset() {
    const params = {
      queryParams: { username: this.loginForm.get('username').value }
    };

    if (this.loginForm.get('username').value) {
      this.#router.navigate(['/resetPassword/'], params);
    } else {
      this.#router.navigate(['/resetPassword']);
    }
  }

  revokeAccessRepairerSite() {
    if (
      this.#currentUserService.currentUserValue.privileges.some((privilege) =>
        ['SYSADMIN', 'MLADMIN'].includes(privilege)
      ) &&
      this.#currentUserService.currentUserValue.privileges.includes('REPADMIN')
    ) {
      this.#sysAdminService.revokeAccessRepairerSite().subscribe();
    } else {
      this.#redirectHelper.manageRedirection(this.#currentUserService.currentUserValue, this.#router);
    }
  }
}
